<template>
    <div class="cookie-consent-wrapper">
    </div>
</template>

<script setup>
    import { onMounted, computed } from 'vue';
    import { useStore } from "vuex";
    import { fbInit, fbTrackPageView } from '@/analytics/facebook-pixel';
    import { gtagInit, grantGtagConsent } from '@/analytics/gtag';
    
    const store = useStore();
    const terms = computed(() => store.state.terms);
    const content = {
        message: terms.value.cookies_info,
        dismiss: terms.value.cookies_close,
        deny: terms.value.cookies_deny,
        allow: terms.value.cookies_allow,
        link: terms.value.cookies_learn_more
    };
    const props = defineProps({
        // CookieConsent visuals / links
        policyUrl: { type: String, default: '/cookies' },
        theme: { type: String, default: 'classic' },
        position: { type: String, default: 'bottom' },
        palette: {
            type: Object,
            default: () => ({
                popup: { background: '#2c2c2c' },
                button: { background: '#4CAF50' }
            })
        },
        // behavior
        typeOptIn: { type: Boolean, default: true },
        revokable: { type: Boolean, default: true }
    });

    const emit = defineEmits(['initialise', 'status-change', 'revoke', 'consent']);

    // IDs from Vite env
    const GA_MEASUREMENT_ID = import.meta.env.VITE_APP_GTAG_ID;
    const GA_SECONDARY_ID = import.meta.env.VITE_APP_GTAG_SECONDARY_ID;
    const FB_PIXEL_ID = import.meta.env.VITE_APP_FBPIXEL_ID;

    // public method
    function openSettings() {
        if (typeof window !== 'undefined' && window.cookieconsent && window.cookieconsent.hasInitialised) {
            window.cookieconsent.revokeChoice()
        }
    }

    // expose for parent via ref
    defineExpose({ openSettings })

    // helpers
    function loadScript(src, id) {
        return new Promise((resolve, reject) => {
            if (document.getElementById(id)) return resolve()
            const s = document.createElement('script')
            s.async = true
            s.src = src
            s.id = id
            s.onload = () => resolve()
            s.onerror = () => reject(new Error('Script load error: ' + src))
            document.head.appendChild(s)
        })
    }

    function loadStylesheet(href, id) {
        return new Promise((resolve, reject) => {
            if (document.getElementById(id)) return resolve()
            const l = document.createElement('link')
            l.rel = 'stylesheet'
            l.href = href
            l.id = id
            l.onload = () => resolve()
            l.onerror = () => reject(new Error('Stylesheet load error: ' + href))
            document.head.appendChild(l)
        })
    }

    function waitFor(checkFn, { attempts = 100, interval = 50 } = {}) {
        return new Promise((resolve, reject) => {
            let tries = 0
            const t = setInterval(() => {
                if (checkFn()) { clearInterval(t); resolve() }
                else if (++tries >= attempts) { clearInterval(t); reject(new Error('Timed out waiting')) }
            }, interval)
        })
    }

    function ensureCookieConsentAssets() {
        const cssUrl = 'https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.css'
        const jsUrl = 'https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.js'
        return Promise.all([
            loadStylesheet(cssUrl, 'cc-osano-css'),
            loadScript(jsUrl, 'cc-osano-js')
        ]).then(() => waitFor(() => window.cookieconsent && window.cookieconsent.initialise))
    }

    function applyConsent(consent) {
        if (consent) {
            if (GA_MEASUREMENT_ID || GA_SECONDARY_ID) 
                grantGtagConsent();
            if (FB_PIXEL_ID) {
                fbInit(FB_PIXEL_ID);
                fbTrackPageView();
            }
            emit('consent', true);
        } else {
            emit('consent', false);
        }
    }

    onMounted(async () => {
        if (typeof window === 'undefined') return

        // Initialize GA in denied/default mode via your util
        const gtags = [GA_MEASUREMENT_ID, GA_SECONDARY_ID].filter(Boolean);
        if (gtags.length > 0) {
            try {                 
                gtagInit(gtags);                
            }
            catch (e) {                 
                console.error('[gtagInit]', e);                
            }
        }

        try {
            await ensureCookieConsentAssets()

            const opts = {
                palette: props.palette,
                theme: props.theme,
                position: props.position,
                revokeBtn: `<div class="cc-revoke cc-left"><i class="mdi mdi-cookie-outline cc-cookie-icon"></i><span>${terms.value.cookies_settings || 'Cookies'}</span></div>`,
                content: { ...content, href: props.policyUrl },
                onInitialise(status) {
                    const consentGiven = this.hasConsented();
                    applyConsent(consentGiven);
                    emit('initialise', { status, consent: consentGiven });
                },
                onStatusChange(status) {
                    const consentGiven = this.hasConsented();
                    applyConsent(consentGiven);
                    emit('status-change', { status, consent: consentGiven });
                },
                onRevokeChoice() {
                    applyConsent(false);
                    emit('revoke');
                }
            }

            window.cookieconsent.initialise(opts);
        } catch (e) {
            console.error('[CookieConsent] init failed', e)
        }
    })
</script>

<style>
.cc-revoke {
    position: fixed !important;
    left: 14px !important;
    bottom: 16px !important;
    z-index: 9991 !important;
    padding: 6px 14px !important;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
    border-radius: 20px !important;
    background: #151515 !important;
    color: #ffc451 !important;
    border: 1.5px solid rgba(255, 196, 81, 0.6) !important;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5) !important;
    backdrop-filter: blur(10px) !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 5px !important;
    cursor: pointer !important;
    transition: all 0.25s ease !important;
}

.cc-cookie-icon {
    font-size: 16px !important;
    color: #ffc451 !important;
}

.cc-revoke:hover {
    background: #ffc451 !important;
    color: #000000 !important;
    transform: scale(1.05) !important;
}

.cc-revoke:hover .cc-cookie-icon {
    color: #000000 !important;
}

/* Mobile Responsiveness (< 600px) - Flush Icon-Only Left Tab */
@media (max-width: 599px) {
    .cc-revoke {
        left: 0px !important;
        bottom: 12px !important;
        width: 36px !important;
        height: 36px !important;
        padding: 0 !important;
        border-radius: 0 18px 18px 0 !important;
        border-left: none !important;
        background: #151515 !important;
        box-shadow: 2px 4px 15px rgba(0, 0, 0, 0.5) !important;
    }

    .cc-revoke span {
        display: none !important;
    }

    .cc-cookie-icon {
        font-size: 20px !important;
        margin-right: 0 !important;
    }
}
</style>
